/**
 * COMMERCE-176A — INLINE delivery profile editor on My Addresses (same page).
 * Primary UX = Nova Poshta delivery method cards (not Home/Work labels).
 */
import { useMemo, useState } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { NovaPoshtaDeliverySection } from "./NovaPoshtaDeliverySection.js";
import type { NovaPoshtaReferenceSelection } from "./nova-poshta-reference-v1.js";
import type {
  CustomerAddressDeliveryType,
  CustomerAddressSource,
  CustomerSavedAddressV2,
} from "./customer-profile-v2.js";
import {
  DELIVERY_METHOD_CARDS,
  defaultProfileLabel,
  deliveryTypeFromMode,
  modeFromDeliveryType,
  type DeliveryMethodId,
} from "./delivery-profile-v2.js";

export type AddressBookEditorDraft = {
  id: string;
  label: string;
  name: string;
  surname: string;
  phone: string;
  city: string;
  warehouse: string;
  is_default: boolean;
  delivery_type: CustomerAddressDeliveryType;
  street?: string;
  building?: string;
  apartment?: string;
  entrance?: string;
  floor?: string;
  intercom?: string;
  comment?: string;
  display_address?: string;
  address_source?: CustomerAddressSource;
  np_selection?: NovaPoshtaReferenceSelection | null;
};

type Props = {
  lang: StoreV2Lang;
  initial: AddressBookEditorDraft;
  onCancel: () => void;
  onSave: (next: CustomerSavedAddressV2) => void;
};

type FieldErrors = Partial<Record<"name" | "surname" | "phone" | "city" | "destination", string>>;

function normalizeUaPhone(raw: string): string {
  const digits = raw.replace(/[^\d+]/g, "");
  if (digits.startsWith("+380") && digits.length >= 13) return digits.slice(0, 13);
  if (digits.startsWith("380") && digits.length >= 12) return `+${digits.slice(0, 12)}`;
  if (digits.startsWith("0") && digits.length >= 10) return `+38${digits.slice(0, 10)}`;
  return digits.slice(0, 20);
}

export function AddressBookEditorSheetV2({ lang, initial, onCancel, onSave }: Props) {
  const en = lang === "en";
  const [method, setMethod] = useState<DeliveryMethodId>(modeFromDeliveryType(initial.delivery_type));
  const [name, setName] = useState(initial.name);
  const [surname, setSurname] = useState(initial.surname);
  const [phone, setPhone] = useState(initial.phone);
  const [city, setCity] = useState(initial.city);
  const [warehouse, setWarehouse] = useState(initial.warehouse);
  const [isDefault, setIsDefault] = useState(initial.is_default);
  const [comment, setComment] = useState(initial.comment ?? "");
  const [entrance, setEntrance] = useState(initial.entrance ?? "");
  const [floor, setFloor] = useState(initial.floor ?? "");
  const [intercom, setIntercom] = useState(initial.intercom ?? "");
  const [manualFallback, setManualFallback] = useState(initial.address_source === "MANUAL");
  const [npSelection, setNpSelection] = useState<NovaPoshtaReferenceSelection | null>(
    initial.np_selection ?? null,
  );
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [npKey, setNpKey] = useState(0);

  const seedSelection = useMemo(() => {
    if (initial.np_selection) return initial.np_selection;
    if (!initial.city && !initial.warehouse) return null;
    return {
      mode: modeFromDeliveryType(initial.delivery_type) === "branch"
        ? "warehouse"
        : modeFromDeliveryType(initial.delivery_type),
      settlementRef: "",
      settlementLabel: initial.city,
      warehouseRef: "",
      warehouseLabel: initial.warehouse,
      warehouseNumber: initial.warehouse,
      addressLine: initial.street,
      building: initial.building,
      flat: initial.apartment,
    } as NovaPoshtaReferenceSelection;
  }, [initial]);

  function selectMethod(next: DeliveryMethodId) {
    if (next === method) return;
    setMethod(next);
    setWarehouse("");
    setNpSelection(null);
    setNpKey((k) => k + 1);
    setFieldErrors({});
  }

  function save() {
    const phoneNorm = normalizeUaPhone(phone);
    const errs: FieldErrors = {};
    if (!name.trim()) errs.name = en ? "Required" : "Обовʼязкове поле";
    if (!surname.trim()) errs.surname = en ? "Required" : "Обовʼязкове поле";
    if (phoneNorm.replace(/\D/g, "").length < 10) {
      errs.phone = en ? "Enter a valid +380 phone" : "Вкажіть коректний телефон +380";
    }
    const delivery_type = deliveryTypeFromMode(npSelection?.mode ?? method);
    const cityOut = (npSelection?.settlementLabel || city).slice(0, 120);
    if (!cityOut.trim()) errs.city = en ? "Select a settlement" : "Оберіть населений пункт";
    const warehouseOut = (
      (npSelection?.mode ?? method) === "address"
        ? [npSelection?.addressLine, npSelection?.building, npSelection?.flat].filter(Boolean).join(", ")
        : npSelection?.warehouseLabel || warehouse
    ).slice(0, 120);
    if (delivery_type !== "NOVA_POSHTA_COURIER" && !warehouseOut.trim()) {
      errs.destination =
        delivery_type === "NOVA_POSHTA_PARCEL_LOCKER"
          ? en
            ? "Select a parcel locker"
            : "Оберіть поштомат"
          : en
            ? "Select a branch"
            : "Оберіть відділення";
    }
    if (delivery_type === "NOVA_POSHTA_COURIER" && !(npSelection?.addressLine || warehouseOut)) {
      errs.destination = en ? "Enter street and building" : "Вкажіть вулицю та будинок";
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setError(en ? "Please fix the highlighted fields" : "Виправте виділені поля");
      return;
    }
    setError(null);
    const address_source: CustomerAddressSource = manualFallback
      ? "MANUAL"
      : "NOVA_POSHTA_DIRECTORY";
    const next: CustomerSavedAddressV2 = {
      id: initial.id,
      label: defaultProfileLabel(lang, delivery_type),
      name: name.trim().slice(0, 80),
      surname: surname.trim().slice(0, 80),
      phone: phoneNorm,
      city: cityOut,
      warehouse: warehouseOut,
      is_default: isDefault,
      updated_at: new Date().toISOString(),
      delivery_type,
      display_address: [cityOut, warehouseOut].filter(Boolean).join(", "),
      address_source,
    };
    if (npSelection?.settlementRef) next.settlement_ref = String(npSelection.settlementRef).slice(0, 80);
    if (npSelection?.addressLine) next.street = String(npSelection.addressLine).slice(0, 120);
    if (npSelection?.building) next.building = String(npSelection.building).slice(0, 40);
    if (npSelection?.flat) next.apartment = String(npSelection.flat).slice(0, 40);
    if (entrance.trim()) next.entrance = entrance.trim().slice(0, 20);
    if (floor.trim()) next.floor = floor.trim().slice(0, 20);
    if (intercom.trim()) next.intercom = intercom.trim().slice(0, 40);
    if (comment.trim()) next.comment = comment.trim().slice(0, 200);
    if (delivery_type === "NOVA_POSHTA_BRANCH") {
      if (npSelection?.warehouseRef) next.branch_ref = String(npSelection.warehouseRef).slice(0, 80);
      if (warehouseOut) next.branch_number = warehouseOut.slice(0, 40);
    }
    if (delivery_type === "NOVA_POSHTA_PARCEL_LOCKER") {
      if (npSelection?.warehouseRef) next.locker_ref = String(npSelection.warehouseRef).slice(0, 80);
      if (warehouseOut) next.locker_number = warehouseOut.slice(0, 40);
    }
    if (npSelection) next.np_selection = npSelection;
    onSave(next);
  }

  return (
    <div
      className="sv2-address-editor-inline sv2-info-card sv2-cabinet-card"
      data-testid="sv2-address-editor"
      data-inline="1"
      data-commerce-176a="1"
      role="region"
      aria-label={en ? "Delivery profile editor" : "Редактор профілю доставки"}
    >
      <div className="sv2-address-editor-title">
        {en ? "Delivery profile" : "Профіль доставки"}
      </div>

      <div className="sv2-address-field" data-testid="sv2-delivery-method-cards">
        <span className="sv2-address-label">
          {en ? "Delivery method *" : "Спосіб доставки *"}
        </span>
        <div className="sv2-delivery-method-cards" role="radiogroup" aria-label={en ? "Delivery method" : "Спосіб доставки"}>
          {DELIVERY_METHOD_CARDS.map((card) => {
            const selected = method === card.id;
            return (
              <button
                key={card.id}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`sv2-delivery-method-card${selected ? " is-selected" : ""}`}
                data-testid={`sv2-delivery-method-${card.id}`}
                onClick={() => selectMethod(card.id)}
              >
                <span className="sv2-delivery-method-icon" aria-hidden>
                  {card.icon}
                </span>
                <span className="sv2-delivery-method-text">
                  <strong>{en ? card.titleEn : card.titleUk}</strong>
                  <span>{en ? card.subtitleEn : card.subtitleUk}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="sv2-address-field-row">
        <label className="sv2-address-field">
          <span className="sv2-address-label">{en ? "First name *" : "Імʼя *"}</span>
          <input
            className={`sv2-input sv2-input-visible${fieldErrors.name ? " is-invalid" : ""}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="given-name"
            placeholder={en ? "e.g. Yaroslav" : "напр. Ярослав"}
            data-testid="sv2-address-name"
            aria-required="true"
          />
          {fieldErrors.name ? <span className="sv2-field-err">{fieldErrors.name}</span> : null}
        </label>
        <label className="sv2-address-field">
          <span className="sv2-address-label">{en ? "Last name *" : "Прізвище *"}</span>
          <input
            className={`sv2-input sv2-input-visible${fieldErrors.surname ? " is-invalid" : ""}`}
            value={surname}
            onChange={(e) => setSurname(e.target.value)}
            autoComplete="family-name"
            placeholder={en ? "e.g. Petrenko" : "напр. Петренко"}
            data-testid="sv2-address-surname"
            aria-required="true"
          />
          {fieldErrors.surname ? <span className="sv2-field-err">{fieldErrors.surname}</span> : null}
        </label>
      </div>

      <label className="sv2-address-field">
        <span className="sv2-address-label">{en ? "Phone *" : "Телефон *"}</span>
        <input
          className={`sv2-input sv2-input-visible${fieldErrors.phone ? " is-invalid" : ""}`}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          inputMode="tel"
          autoComplete="tel"
          placeholder="+380XXXXXXXXX"
          data-testid="sv2-address-phone"
          aria-required="true"
        />
        {fieldErrors.phone ? <span className="sv2-field-err">{fieldErrors.phone}</span> : null}
      </label>

      <div className="sv2-address-field" data-testid="sv2-address-np-section">
        {fieldErrors.city ? <span className="sv2-field-err">{fieldErrors.city}</span> : null}
        {fieldErrors.destination ? <span className="sv2-field-err">{fieldErrors.destination}</span> : null}
        <NovaPoshtaDeliverySection
          key={`np-${method}-${npKey}`}
          lang={lang}
          initialSelection={
            seedSelection && modeFromDeliveryType(initial.delivery_type) === method
              ? seedSelection
              : method === "address"
                ? ({ mode: "address", settlementRef: "", settlementLabel: city } as NovaPoshtaReferenceSelection)
                : method === "parcel_locker"
                  ? ({ mode: "parcel_locker", settlementRef: "", settlementLabel: city } as NovaPoshtaReferenceSelection)
                  : ({ mode: "warehouse", settlementRef: "", settlementLabel: city } as NovaPoshtaReferenceSelection)
          }
          forcedMode={method}
          hideModeTabs
          manualFallback={manualFallback}
          onManualFallbackChange={setManualFallback}
          onSelectionChange={(sel) => {
            setNpSelection(sel);
            if (sel?.settlementLabel) setCity(sel.settlementLabel);
            if (sel?.warehouseLabel) setWarehouse(sel.warehouseLabel);
          }}
          onDisplayChange={(c, w) => {
            setCity(c);
            setWarehouse(w);
          }}
        />
      </div>

      {method === "address" ? (
        <div className="sv2-address-field-row" data-testid="sv2-courier-extra">
          <label className="sv2-address-field">
            <span className="sv2-address-label">{en ? "Entrance" : "Підʼїзд"}</span>
            <input className="sv2-input sv2-input-visible" value={entrance} onChange={(e) => setEntrance(e.target.value)} />
          </label>
          <label className="sv2-address-field">
            <span className="sv2-address-label">{en ? "Floor" : "Поверх"}</span>
            <input className="sv2-input sv2-input-visible" value={floor} onChange={(e) => setFloor(e.target.value)} />
          </label>
          <label className="sv2-address-field">
            <span className="sv2-address-label">{en ? "Intercom" : "Домофон"}</span>
            <input className="sv2-input sv2-input-visible" value={intercom} onChange={(e) => setIntercom(e.target.value)} />
          </label>
        </div>
      ) : null}

      <label className="sv2-address-field">
        <span className="sv2-address-label">{en ? "Courier comment" : "Коментар курʼєру"}</span>
        <textarea
          className="sv2-input sv2-input-visible sv2-address-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          data-testid="sv2-address-comment"
          rows={2}
          placeholder={en ? "Optional" : "Необовʼязково"}
        />
      </label>

      <label className="sv2-address-default">
        <input
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
          data-testid="sv2-address-default"
        />
        {en ? "Set as primary" : "Зробити основною"}
      </label>

      {error ? (
        <p className="sv2-error" data-testid="sv2-address-error">
          {error}
        </p>
      ) : null}

      <div className="sv2-address-actions" data-testid="sv2-address-actions">
        <button type="button" className="sv2-cta sv2-secondary sv2-cta-compact" data-testid="sv2-address-cancel" onClick={onCancel}>
          {en ? "Cancel" : "Скасувати"}
        </button>
        <button type="button" className="sv2-cta sv2-cta-compact" data-testid="sv2-address-save" onClick={save}>
          {en ? "Save profile" : "Зберегти адресу"}
        </button>
      </div>
    </div>
  );
}
